import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { Button } from './Button';

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]):not([type="hidden"]):not([tabindex="-1"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * A modal dialog: a surface card on a dimmed base backdrop, no shadow. Escape and the close button
 * call onClose (unless `busy`); focus moves to its first control, stays inside, and returns afterwards.
 * Clicking the backdrop does nothing, so a half-filled form isn't lost by a stray click.
 */
export const Dialog = ({ open, onClose, title, subtitle, busy = false, children }) => {
  const titleId = useId();
  const panelRef = useRef(null);
  const bodyRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const busyRef = useRef(busy);
  busyRef.current = busy;

  useEffect(() => {
    if (!open) return undefined;
    const previouslyFocused = document.activeElement;
    const panel = panelRef.current;
    // The first control in its content gets focus, or else the dialog itself
    const first = bodyRef.current.querySelector(FOCUSABLE);
    (first || panel).focus();
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !busyRef.current) {
        event.stopPropagation();
        onCloseRef.current();
      } else if (event.key === 'Tab') {
        const focusable = [...panel.querySelectorAll(FOCUSABLE)];
        if (focusable.length === 0) return;
        const [head, tail] = [focusable[0], focusable[focusable.length - 1]];
        if (event.shiftKey && document.activeElement === head) {
          event.preventDefault();
          tail.focus();
        } else if (!event.shiftKey && document.activeElement === tail) {
          event.preventDefault();
          head.focus();
        }
      }
    };
    panel.addEventListener('keydown', onKeyDown);
    return () => {
      panel.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = overflow;
      previouslyFocused?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[100] overflow-y-auto bg-base/80 p-space-4 flex items-start sm:items-center justify-center">
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="w-full max-w-[640px] my-auto flex flex-col gap-space-6 p-space-6 sm:p-space-8 rounded-radius-xl bg-surface border border-line text-ink font-text focus:outline-none"
      >
        <div className="flex items-start justify-between gap-space-4">
          <div className="flex flex-col gap-space-1 min-w-0">
            <h2 id={titleId} className="m-0 font-display text-title-2 text-ink">{title}</h2>
            {subtitle && <span className="text-small text-muted">{subtitle}</span>}
          </div>
          <Button variant="icon" icon={X} aria-label="Close" onClick={onClose} disabled={busy} className="-mr-space-2 -mt-space-1" />
        </div>
        <div ref={bodyRef} className="contents">{children}</div>
      </div>
    </div>,
    document.body
  );
};

/** A labelled text field in a dialog, with an optional hint after the label */
export const Field = ({ id, label, hint, children }) => (
  <div className="flex flex-col gap-space-2">
    <label htmlFor={id} className="text-label text-ink">
      {label}
      {hint && <span className="font-normal text-muted"> {hint}</span>}
    </label>
    {children}
  </div>
);

// Inputs: base ground, control border, ink text
export const inputClass =
  'w-full min-h-target-min px-space-3 rounded-radius-md bg-base border border-control text-body text-ink placeholder:text-muted ' +
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus';
